import { createSlice } from '@reduxjs/toolkit';
// utils
import axios from '../../utils/axios';
import { getCurrentOrDefaultPeriod, publishedPeriodFormatter, processChatParams } from '../../utils/core';
import { getUserPyramid } from './user';

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  error: false,
  notifications: null,
  screens: null,
  editSelectedScreen: null,
  selectedItem: null,
  activeScreen: null,
  screenFormOpen: false,
  itemFormOpen: false,
  publishedPeriods: null
};

const slice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    setScreens(state, action) {
      state.isLoading = false;
      state.screens = action.payload;
    },

    setActiveScreen(state, action) {
      state.isLoading = false;
      state.activeScreen = action.payload;
    },

    setPublishedPeriods(state, action) {
      // state.isLoading = false;
      state.publishedPeriods = action.payload;
    },

    setScreenFormOpen(state, action) {
      state.screenFormOpen = action.payload;
    },

    setItemFormOpen(state, action) {
      state.itemFormOpen = action.payload;
    },

    setEditSelectedScreen(state, action) {
      state.editSelectedScreen = action.payload;
    },

    setEditSelectedItem(state, action) {
      state.selectedItem = action.payload;
    },

    notificationSuccess(state, action) {
      state.notifications = action.payload;
    }
  }
});

// Reducer
export default slice.reducer;

// Actions
export const {
  setActiveScreen,
  setPublishedPeriods,
  setEditSelectedScreen,
  setEditSelectedItem,
  setScreenFormOpen,
  setItemFormOpen,
  notificationSuccess,
  setScreens
} = slice.actions;

// ----------------------------------------------------------------------

export function getDashboardScreens() {
  return async (dispatch, getState) => {
    dispatch(slice.actions.startLoading());
    try {
      let formattedResp = getState().dashboard.publishedPeriods;
      if (!formattedResp) {
        const periods = await axios.get(`/api/data/periods`);
        if (periods !== null) {
          formattedResp = periods.data.map(publishedPeriodFormatter);
          dispatch(slice.actions.setPublishedPeriods(formattedResp));
        }
      }
      const response = await axios.get('/api/dashboards');
      if (
        response !== null &&
        response.data !== undefined &&
        response.data.length > 0 &&
        response.data[0].items &&
        response.data[0].items.length > 0
      ) {
        dispatch(getUserPyramid({ entities: response.data[0].items[0].entity_id }));
      }
      const patchedScreens = (response.data || []).map((screen) => ({
        ...screen,
        items: (screen.items || []).map((item) => ({
          ...item,
          dataLabels: !!['donut', 'pie', 'stackedBar', 'verticalStackedBar'].includes(item.chart_type),
          period: getCurrentOrDefaultPeriod(
            (formattedResp || []).filter((resp) => resp.kind === item.kind),
            item.period_slider_type,
            {}
          )
        }))
      }));
      dispatch(slice.actions.setScreens(patchedScreens));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getPublishedPeriods() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(`/api/data/periods`);
      const formattedResp = response.data.map(publishedPeriodFormatter);
      dispatch(slice.actions.setPublishedPeriods(formattedResp));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function createUpdateScreen(params) {
  return async (dispatch, getState) => {
    dispatch(slice.actions.startLoading());
    try {
      if (params.id === undefined || params.id === '') {
        await axios.post('/api/dashboards', { dashboard: params });
      } else {
        await axios.put(`/api/dashboards/${params.id}`, { dashboard: params });
      }
      const promises = [dispatch(getDashboardScreens()), dispatch(setScreenFormOpen(false))];
      await Promise.all(promises).then(() => {
        const { screens } = getState().dashboard;
        if (params.id === undefined || params.id === '') {
          dispatch(
            setActiveScreen(Number(params.sequence) > screens.length ? screens.length : Number(params.sequence))
          );
        } else {
          const screenIndex = screens.findIndex((sreen) => sreen.id === params.id);
          dispatch(setActiveScreen(screenIndex));
        }
      });
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function removeScreen(id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      await axios.delete(`/api/dashboards/${id}`);

      const promises = [dispatch(getDashboardScreens())];
      await Promise.all(promises).then(() => {
        dispatch(setActiveScreen(0));
      });
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function createUpdateScreenItem(params) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      let response = null;
      if (params.id === undefined || params.id === '') {
        response = await axios.post('/api/dashboards/item', { dashboard_item: params });
      } else {
        response = await axios.put(`/api/dashboards/item/${params.id}`, { dashboard_item: params });
      }
      if (response !== null && response.data !== undefined) {
        dispatch(patchScreenItem(response.data));
        dispatch(setItemFormOpen(false));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function patchScreenItem(newScreens) {
  return (dispatch, getState) => {
    const { screens, publishedPeriods } = getState().dashboard;
    const { userPyramid } = getState().user;
    const existingScreens = { ...screens };

    const patchedScreens = newScreens.map((screen, screenId) => ({
      ...screen,
      items: screen.items.map((item) => {
        const existingItem = existingScreens[screenId].items.find((exItem) => exItem.id === item.id);
        return {
          ...item,
          isLoading: existingItem !== undefined && existingItem.isLoading !== undefined ? existingItem.isLoading : true,
          data: existingItem !== undefined && existingItem.data !== undefined ? existingItem.data : [],
          period:
            existingItem !== undefined && existingItem.period !== undefined
              ? existingItem.period
              : getCurrentOrDefaultPeriod(
                  publishedPeriods.filter((resp) => resp.kind === item.kind),
                  item.period_slider_type,
                  {}
                ),
          touched: existingItem !== undefined && existingItem.touched !== undefined ? existingItem.touched : true, // for immediate launch of the default tab generic blocks
          pyramid:
            existingItem !== undefined && existingItem.pyramid !== undefined
              ? existingItem.pyramid
              : {
                  tree: [{ ...userPyramid.tree[0], checked: true }],
                  selected: item.entity_id
                }
        };
      })
    }));
    dispatch(setScreens(patchedScreens));
  };
}

export function updateScreenItem(updates) {
  return (dispatch, getState) => {
    const { screens } = getState().dashboard;
    if (!screens || !Array.isArray(screens) || updates.length === 0) return;

    const updatedScreens = screens.map((screen, sIdx) => {
      const screenUpdates = updates.filter((u) => u.screenId === sIdx);
      if (screenUpdates.length === 0) return screen;

      const updatedItems = (screen.items || []).map((item) => {
        const itemUpdates = screenUpdates.filter((u) => u.itemId === item.id);
        if (itemUpdates.length === 0) return item;

        const patch = {};
        itemUpdates.forEach((u) => {
          patch[u.field] = u.value;
        });
        return { ...item, ...patch };
      });

      return { ...screen, items: updatedItems };
    });

    dispatch(setScreens(updatedScreens));
  };
}

export function deleteScreenItem(id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    const response = await axios.delete(`/api/dashboards/item/${id}`);
    if (response !== null && response.data !== undefined) {
      dispatch(patchScreenItem(response.data));
    }
  };
}

export function setNotificationItems(items) {
  return async (dispatch, getState) => {
    const { notifications } = getState().dashboard;
    try {
      dispatch(slice.actions.notificationSuccess({ ...notifications, items }));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function notificationKeys() {
  return async (dispatch) => {
    try {
      const response = await axios.get('/api/data/consistency');
      dispatch(slice.actions.notificationSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function notificationCheck(check = undefined) {
  return async () => {
    try {
      const response = await axios.get('/api/data/consistency', { params: { check } });
      return response?.data?.items;
    } catch (error) {
      return error;
    }
  };
}

export function notificationFix(key) {
  return async (dispatch) => {
    try {
      const response = await axios.get('/api/data/consistency', {
        params: { key }
      });
      dispatch(slice.actions.notificationSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getDashBoardBlock(params) {
  return async (dispatch, getState) => {
    const { screens } = getState().dashboard;
    dispatch(
      updateScreenItem([{ value: true, field: 'isLoading', screenId: params.screenId, itemId: params.itemParams.id }])
    );

    try {
      const itemParams = screens
        .map((screen) => screen.items)
        .flat()
        .find((item) => item.id === params.itemParams.id);
      if (itemParams !== undefined) {
        const p = processChatParams(itemParams);
        if (
          p.entity_id !== undefined &&
          p.entity_id.length > 0 &&
          p.start_date !== undefined &&
          p.end_date !== undefined &&
          // p.start_quarter !== undefined &&
          // p.end_quarter !== undefined &&
          p.ids !== undefined
        ) {
          const response = await axios.post(`/api/data/chart`, { formula_value: p });

          dispatch(
            updateScreenItem([
              { value: response.data, field: 'data', screenId: params.screenId, itemId: params.itemParams.id },
              { value: false, field: 'isLoading', screenId: params.screenId, itemId: params.itemParams.id },
              { value: false, field: 'touched', screenId: params.screenId, itemId: params.itemParams.id }
            ])
          );
        } else {
          dispatch(
            updateScreenItem([
              { value: false, field: 'isLoading', screenId: params.screenId, itemId: params.itemParams.id },
              { value: false, field: 'touched', screenId: params.screenId, itemId: params.itemParams.id }
            ])
          );
        }
      } else {
        dispatch(
          updateScreenItem([
            { value: false, field: 'isLoading', screenId: params.screenId, itemId: params.itemParams.id },
            { value: false, field: 'touched', screenId: params.screenId, itemId: params.itemParams.id }
          ])
        );
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      dispatch(
        updateScreenItem([
          { value: false, field: 'isLoading', screenId: params.screenId, itemId: params.itemParams.id },
          { value: false, field: 'touched', screenId: params.screenId, itemId: params.itemParams.id }
        ])
      );
    }
  };
}

export function getDashBoardItem(params) {
  return async (dispatch) => {
    dispatch(
      updateScreenItem([{ value: true, field: 'isLoading', screenId: params.screenId, itemId: params.itemParams.id }])
    );
    try {
      const p = processChatParams(params.itemParams);

      if (
        p.entity_id !== undefined &&
        p.entity_id.length > 0 &&
        p.start_date !== undefined &&
        p.end_date !== undefined &&
        p.ids !== undefined
      ) {
        const response = await axios.post(`/api/data/chart`, { formula_value: p });
        return { screenId: params.screenId, itemId: params.itemParams.id, data: response.data };
      }
    } catch (error) {
      return { screenId: params.screenId, itemId: params.itemParams.id, data: null };
    }
    return { screenId: params.screenId, itemId: params.itemParams.id, data: null };
  };
}
